import {
  areSchedulesHorizontallyOverlapping,
  areSchedulesOverlapping,
  centerOfMass,
  Group,
} from "../state/data/utils";
import { ScaleTime } from "d3-scale";
import {
  addMinutes,
  areIntervalsOverlapping,
  isWithinInterval,
  subMinutes,
} from "date-fns";
import * as R from "ramda";
import { ExactlyScheduledNote, ExactSchedule, Note } from "../state/data";

const noteToInterval = (n: ExactlyScheduledNote) => {
  return R.pick(["start", "end"], n.schedule) as { start: Date; end: Date };
};

const findAvailableSpace = (
  notes: ExactlyScheduledNote[],
  interval: Interval
) => {
  // Relevant for computation: all edges.
  // console.log("finding available in = ", notes);
  const pointsOfInterest = R.uniq([
    interval.start,
    interval.end,
    ...notes
      .flatMap((n) => [n.schedule.start, n.schedule.end])
      .filter((d) => isWithinInterval(d, interval)),
  ]);
  // console.log("pois =", pointsOfInterest);

  const constraints = pointsOfInterest.map((poi) => {
    const notesHere = R.sortBy(
      (a) => a.schedule.offset,
      notes.filter((n) => isWithinInterval(poi, noteToInterval(n)))
    );
    let width = 1 - R.sum(notesHere.map((n) => n.schedule.width));

    let offset = 0;
    notesHere.forEach((n) => {
      if (
        // there is a collision...
        areSchedulesHorizontallyOverlapping({ offset, width }, n.schedule)
      ) {
        // try to place it after.
        offset = n.schedule.offset + n.schedule.width;
        if (offset + width > 1) {
          width = 1 - offset;
        }
      } else {
        console.log("no collision with", n.title);
      }
    });

    return { offset, width };
  });

  const choice = R.head(R.sortBy((c) => c.width, constraints))!;

  return choice;
};
const makeAvailableSpace = (
  interval: { start: Date; end: Date },
  notes: ExactlyScheduledNote[]
) => {
  const pointsOfInterest = R.uniq([
    interval.start,
    interval.end,
    ...notes
      .flatMap((n) => [n.schedule.start, n.schedule.end])
      .filter((d) => isWithinInterval(d, interval)),
  ]);

  const possibleWidths = pointsOfInterest.map((poi) => {
    const notesHere = notes.filter((n) =>
      isWithinInterval(poi, noteToInterval(n))
    );
    return 1 / notesHere.length;
  });

  const chosenWidth = Math.min(...possibleWidths);

  let reflowedNotes = notes;

  pointsOfInterest.forEach((poi) => {
    const notesHere = R.sortBy(
      (a) => a.schedule.offset,
      notes.filter((n) => isWithinInterval(poi, noteToInterval(n)))
    );
    const takenWidth = R.sum(notesHere.map((n) => n.schedule.width));
    if (takenWidth + chosenWidth > 1) {
      const smalleringFactor = 1 / (takenWidth + chosenWidth);
      R.reduce(
        (acc, el) => {
          const newWidth = el.schedule.width * smalleringFactor;
          const newOffset = acc;
          reflowedNotes = R.adjust(
            R.findIndex((n) => n.id === el.id, reflowedNotes),
            (n) => ({
              ...n,
              schedule: {
                ...n.schedule,
                width: newWidth,
                offset: newOffset,
              },
            }),
            reflowedNotes
          );
          return acc + newWidth;
        },
        0,
        notesHere
      );
    }
  });

  const found = findAvailableSpace(reflowedNotes, interval);

  return {
    schedule: { ...interval, ...found },
    displaced: reflowedNotes,
  };
};
export function scheduleFromResize(
  direction: string,
  size: { height: number; width: number },
  n: ExactlyScheduledNote,
  scale: ScaleTime<number, number, never>,
  colWidth: number
): ExactSchedule {
  switch (direction) {
    case "top":
      return {
        ...n.schedule,
        start: scale.invert(scale(n.schedule.start) - size.height),
      };
    case "bottom":
      return {
        ...n.schedule,
        end: scale.invert(scale(n.schedule.end) + size.height),
      };
    case "left":
      return {
        ...n.schedule,
        offset: n.schedule.offset - size.width / colWidth,
        width: n.schedule.width + size.width / colWidth,
      };
    case "right":
      return {
        ...n.schedule,
        width: n.schedule.width + size.width / colWidth,
      };
    case "topLeft":
      return {
        ...n.schedule,
        start: scale.invert(scale(n.schedule.start) - size.height),
        offset: n.schedule.offset - size.width / colWidth,
        width: n.schedule.width + size.width / colWidth,
      };
    case "topRight":
      return {
        ...n.schedule,
        start: scale.invert(scale(n.schedule.start) - size.height),
        width: n.schedule.width + size.width / colWidth,
      };
    case "bottomRight":
      return {
        ...n.schedule,
        end: scale.invert(scale(n.schedule.end) + size.height),
        width: n.schedule.width + size.width / colWidth,
      };
    case "bottomLeft":
      return {
        ...n.schedule,
        end: scale.invert(scale(n.schedule.end) + size.height),
        offset: n.schedule.offset - size.width / colWidth,
        width: n.schedule.width + size.width / colWidth,
      };
  }
  return n.schedule;
}
const makeWayFor =
  (schedule: ExactSchedule) => (note: ExactlyScheduledNote) => {
    const start = schedule.offset;
    const end = schedule.offset + schedule.width;

    let candidate: ExactSchedule;

    if (centerOfMass(note.schedule) >= centerOfMass(schedule)) {
      // push to right
      const offset = Math.max(end, note.schedule.offset);
      candidate = {
        ...note.schedule,
        offset,
        width:
          offset + note.schedule.width > 1 ? 1 - offset : note.schedule.width,
      };
    } else {
      // push to left
      const offset = Math.max(start - note.schedule.width, 0);
      candidate = {
        ...note.schedule,
        offset,
        width:
          offset + note.schedule.width > start ? start : note.schedule.width,
      };
    }

    // reject bad candidates and do nothing, always a decent default choice
    if (candidate.width < 0.2) {
      return { ...note };
    }

    return { ...note, schedule: candidate };
  };
export function displaceFromExactSchedule(
  schedule: ExactSchedule,
  notes: ExactlyScheduledNote[],
  groups: Group[],
  colWidth: number
) {
  const touchGroups = groups.filter((g) =>
    areIntervalsOverlapping(
      R.pick(["start", "end"], schedule),
      R.pick(["start", "end"], g)
    )
  );
  const touchGroupsNoteIds = R.uniq(touchGroups.flatMap((g) => g.ids));

  const displaced = notes
    .filter((n) => R.includes(n.id, touchGroupsNoteIds))
    .filter((n) => areSchedulesOverlapping(n.schedule, schedule))
    .map(makeWayFor(schedule));

  const springSetter = makeSpringSetter(notes, displaced, colWidth);
  return { springSetter, displaced };
}
export function processPlaceholderScheduling(
  scheduleTo: { start: Date; end: Date },
  groups: Group[],
  notes: ExactlyScheduledNote[],
  colWidth: number
) {
  const collider = R.pick(["start", "end"], scheduleTo);
  // const adjustedCollider = {
  //   start: addMinutes(collider.start, 5),
  //   end: subMinutes(collider.end, 5),
  // };

  const collisionsWith = R.head(
    // INEX: you can collision with >1 group
    groups.filter((g) =>
      areIntervalsOverlapping(collider, R.pick(["start", "end"], g))
    )
  );
  if (collisionsWith === undefined) {
    // Scenario 1: no collision, take full space
    return { schedule: { ...scheduleTo, width: 1, offset: 0 } };
  } else {
    const collidingNotes = notes
      .filter((n) => R.includes(n.id, collisionsWith.ids))
      .filter((n) => areIntervalsOverlapping(noteToInterval(n), scheduleTo));

    const available = findAvailableSpace(collidingNotes, scheduleTo);
    if (available.width > 0.2) {
      // Scenario 2: Collisions, enough available space, taking it..
      return {
        schedule: {
          ...scheduleTo,
          ...available,
        },
      };
    } else {
      // Scenario 3: Collisions, not enough space, creating some by modifying existing notes..
      const { schedule, displaced } = makeAvailableSpace(
        scheduleTo,
        notes.filter((n) => R.includes(n.id, collisionsWith.ids))
      );
      // ... and the rest
      const springSetter = makeSpringSetter(notes, displaced, colWidth);
      return { schedule, springSetter, displaced };
    }
  }
}

function makeSpringSetter(
  notes: ExactlyScheduledNote[],
  displaced: ExactlyScheduledNote[],
  colWidth: number
) {
  return (index) => {
    if (notes[index] === undefined) {
      return { x: 0, width: 1 }; // TODO: shouldn't be here..
    }
    if (
      !R.includes(
        notes[index].id,
        displaced.map((n) => n.id)
      )
    ) {
      return {
        x: notes[index].schedule.offset * colWidth,
        width: notes[index].schedule.width * colWidth,
      };
    } else {
      const reflowed = displaced.find(R.propEq("id", notes[index].id))!;
      return {
        x: reflowed.schedule.offset * colWidth,
        width: reflowed.schedule.width * colWidth,
      };
    }
  };
}
